import Timezone from 'timezone';
import NSW from 'timezone/Australia/NSW';
import West from 'timezone/Australia/West';
import ACT from 'timezone/Australia/ACT';
import North from 'timezone/Australia/North';
import South from 'timezone/Australia/South';
import Tasmania from 'timezone/Australia/Tasmania';
import Victoria from 'timezone/Australia/Victoria';
import Queensland from 'timezone/Australia/Queensland';
import { config } from '##/services/store/store';

interface Zone {
  rules: {
    [key: string]: unknown[];
  };
  zones: {
    [timezoneKey: string]: unknown[];
  };
}

export const expiryToString = (expiry: number): string => {
  // round up for minutes and hours because the transitions
  // from (for example) minutes to seconds is smoother
  // (e.g. 61 seconds = 2 minutes remaining, 60 seconds = 1
  // minutes remaining and 59 seconds is 59 seconds)
  //
  // round to nearest whole number for later dates because that
  //  gives a better idea of the general timeframe

  const diff = expiry - Math.floor(new Date().getTime() / 1000);
  if (diff < 0) return 'Expired';
  if (diff < 60) return `In ${diff} seconds`;
  if (diff < 60 * 60) return `In ${Math.ceil(diff / 60)} mins`;
  if (diff < 60 * 60 * 24) return `In ${Math.ceil(diff / 60 / 60)} hrs`;
  if (diff < 60 * 60 * 24 * 7) {
    return `In ${Math.round(diff / 60 / 60 / 24)} days`;
  }
  if (diff < 60 * 60 * 24 * 31) {
    return `In ${Math.round(diff / 60 / 60 / 24 / 7)} weeks`;
  }
  if (diff < 60 * 60 * 24 * 365) {
    return `In ${Math.round(diff / 60 / 60 / 24 / 31)} months`;
  }
  return `In ${Math.round(diff / 60 / 60 / 24 / 365)} years`;
};

export const secondsToProgressDisplay = (time: number): string => {
  const seconds = `0${Math.floor(time % 60)}`.slice(-2);
  const minutes = `0${Math.floor(time / 60) % 60}`.slice(-2);
  const hours = `0${Math.floor(time / 60 / 60)}`.slice(-2);
  return time > 60 * 60
    ? `${hours}:${minutes}:${seconds}`
    : `${minutes}:${seconds}`;
};

const getRegionZoneObj = (): Zone => {
  const { region = 'NSW' } = config.$get().geo;
  const timezoneMap: Record<string, Zone> = {
    nsw: NSW,
    wa: West,
    act: ACT,
    nt: North,
    sa: South,
    tas: Tasmania,
    vic: Victoria,
    qld: Queensland,
  };
  return timezoneMap[region.toLowerCase()] || timezoneMap.nsw;
};

export const getTealiumLocalTimestamp = (unix: number): string => {
  const timezone = getRegionZoneObj();
  const timezoneKey = Object.keys(timezone.zones)[0];
  const dateObj = Timezone(timezone);
  return dateObj(unix, timezoneKey, '%Y-%m-%dT%H:%M:%S');
};

export const getTealiumUTCTimestamp = (unix: number): string => {
  const dateObj = Timezone('UTC');
  return dateObj(unix, '', '%Y-%m-%dT%H:%M:%S');
};

export const getFormattedUnixTimestamp = (unix: number): string => {
  try {
    const timezone = getRegionZoneObj();
    const timezoneKey = Object.keys(timezone.zones)[0];
    const auZone = Timezone(timezone);
    const timeString = auZone(unix * 1000, timezoneKey, '%I.%M%P');
    return timeString.startsWith('0') ? timeString.slice(1) : timeString;
  } catch (err) {
    // fallback to whatever the computer time is
    const date = new Date(unix * 1000);
    const hours = date.getHours() % 12 || 12;
    const minutes = `0${date.getMinutes()}`;
    const ampm = date.getHours() / 12 >= 1 ? 'pm' : 'am';
    return `${hours}:${minutes.slice(-2)}${ampm}`;
  }
};

export const formatTime = (time: number): string => {
  const timeInSeconds = Math.floor(time);
  const hour = Math.floor(timeInSeconds / 3600);
  const min = Math.floor((timeInSeconds % 3600) / 60);
  const hourText = hour === 1 ? 'hour' : 'hours';
  const minText = min === 1 ? 'min' : 'minutes';
  return `${(hour > 0 ? `${hour} ${hourText} ` : '') + min} ${minText}`;
};

const weekdays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const getUnixToDate = (unixTime?: number): string => {
  if (!unixTime) return '';
  const newDate = new Date(unixTime * 1000);

  try {
    const month = months[newDate.getMonth()].slice(0, 3);
    const day = weekdays[newDate.getDay()].slice(0, 3);
    const year = newDate.getFullYear();
    return `${day} ${newDate.getDate()} ${month} ${year}`;
  } catch {
    return '';
  }
};
